import React, { useEffect } from 'react';
import styled from 'styled-components';

import Select from 'components/ui/SelectField';
import { Input } from '@yuhu-inc/yuhui';

import NumberFormat from 'react-number-format';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

const StyledFormContainer = styled.div``;

const StyledFormRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StyledFormItem = styled.div`
  width: 100%;
  margin: 0px 8px;
`;

const StyledFormItemLabel = styled.div`
  margin-bottom: 5px;
`;

const StyledInput = styled(Input)`
  border-radius: 5px !important;
  border: 1px solid #d0dbfd !important;
  border-color: ${(props) => (props.isError ? `${colors.YUHU_RED} !important` : 'inherit')};

  &:focus {
    outline: none !important;
    border-color: #230cc2 !important;
  }

  ::placeholder {
    color: #bfbfbf;
    opacity: 1;
  }
`;

const StyledNumberInput = styled(NumberFormat)`
  width: 100%;
  border-radius: 5px !important;
  border: 1px solid #d0dbfd !important;
  border-color: ${(props) => (props.isError ? `${colors.YUHU_RED} !important` : 'inherit')};

  padding: 7px 10px;
  &:focus {
    outline: none !important;
    border-color: #230cc2 !important;
  }
  ::placeholder {
    color: #bfbfbf;
    opacity: 1;
  }
`;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
`;

export const CreateNewPurchaseOrderCatalogItemForm = ({
  setFieldValue,
  new_purchase_order_item,
  units,
  formErrors,
}) => {
  useEffect(() => {
    if (new_purchase_order_item.quantity && new_purchase_order_item.estimated_cost) {
      let totalCost =
        Math.round(
          new_purchase_order_item.estimated_cost * new_purchase_order_item.quantity * 100
        ) / 100;
      setFieldValue('new_purchase_order_item.total_cost', totalCost);
    }
  }, [new_purchase_order_item.quantity, new_purchase_order_item.estimated_cost, setFieldValue]);

  const setTotalCostOnItem = (quantity, estimated_cost) => {
    if (!quantity && !estimated_cost) return;
    let totalCost = Math.round(estimated_cost * quantity * 100) / 100;
    setFieldValue('new_purchase_order_item.total_cost', totalCost);
  };

  return (
    <StyledFormContainer>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.createNewPurchaseOrderCatalogItem.indicates')}
          </StyledFormItemLabel>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.createNewPurchaseOrderCatalogItem.itemName')}*
          </StyledFormItemLabel>
          <StyledInput
            type="text"
            value={new_purchase_order_item.item_name}
            placeholder={getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.itemNamePlaceholder'
            )}
            onChange={(e) => {
              setFieldValue('new_purchase_order_item.item_name', e.target.value);
              setFieldValue('new_catalog_form_errors.item_name', false);
            }}
            style={{ padding: '7px 10px' }}
            isError={formErrors.item_name}
          />
          {formErrors.item_name && <StyledError>{getTranslatedString('required')}</StyledError>}
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.itemDescription'
            )}
            *
          </StyledFormItemLabel>
          <StyledInput
            type="text"
            value={new_purchase_order_item.item_description}
            placeholder={getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.itemDescriptionPlaceholder'
            )}
            onChange={(e) => {
              setFieldValue('new_purchase_order_item.item_description', e.target.value);
              setFieldValue('new_catalog_form_errors.item_description', false);
            }}
            style={{ padding: '7px 10px' }}
            isError={formErrors.item_description}
          />
          {formErrors.item_description && (
            <StyledError>{getTranslatedString('required')}</StyledError>
          )}
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.createNewPurchaseOrderCatalogItem.quantity')}*
          </StyledFormItemLabel>
          <StyledNumberInput
            value={new_purchase_order_item.quantity}
            decimalScale={0}
            placeholder={getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.itemQuantityPlaceholder'
            )}
            onChange={(e) => {
              setFieldValue('new_purchase_order_item.quantity', parseInt(e.target.value));
              setTotalCostOnItem(e.target.value, new_purchase_order_item.estimated_cost);
              setFieldValue('new_catalog_form_errors.quantity', false);
            }}
            isError={formErrors.quantity}
          />
          {formErrors.quantity && <StyledError>{getTranslatedString('required')}</StyledError>}
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.estimatedCostPerItem'
            )}
            *
          </StyledFormItemLabel>
          <StyledNumberInput
            value={new_purchase_order_item.estimated_cost}
            prefix="$"
            decimalScale={2}
            placeholder={getTranslatedString(
              'landlordPortal.createNewPurchaseOrderCatalogItem.itemCostPlaceholder'
            )}
            onChange={(e) => {
              setFieldValue(
                'new_purchase_order_item.estimated_cost',
                e.target.value.replace('$', '') // removes prefix
              );
              setTotalCostOnItem(new_purchase_order_item.quantity, e.target.value.replace('$', ''));
              setFieldValue('new_catalog_form_errors.estimated_cost', false);
            }}
            isError={formErrors.estimated_cost}
          />
          {formErrors.estimated_cost && (
            <StyledError>{getTranslatedString('required')}</StyledError>
          )}
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.createNewPurchaseOrderCatalogItem.unit')}
          </StyledFormItemLabel>
          <Select
            showSearch
            placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
            size="large"
            value={new_purchase_order_item.unit_id}
            onChange={(value) => {
              setFieldValue('new_purchase_order_item.unit_id', value);
              setFieldValue(
                'new_purchase_order_item.unit_number',
                units.find((unit) => unit.value === value).label
              );
            }}
          >
            {units.map((unit) => (
              <Select.Option value={unit.value} key={`unit_number_${unit.value}`}>
                {unit.label}
              </Select.Option>
            ))}
          </Select>
        </StyledFormItem>
      </StyledFormRow>
    </StyledFormContainer>
  );
};
